// InvestmentService.ts - Comprehensive CRUD service for investment management

import { 
  Investment, 
  InvestmentFormData, 
  InvestmentFilters, 
  InvestmentSortOptions,
  InvestmentBulkOperation,
  InvestmentValidationResult,
  InvestmentImportResult
} from '../types/Investment';
import { investments as defaultInvestments } from '../data/investments';

export class InvestmentService {
  private static readonly STORAGE_KEY = 'risk-game-investments';

  // Core CRUD Operations
  static async getAll(
    filters?: InvestmentFilters,
    sort?: InvestmentSortOptions,
    pagination?: { page: number; limit: number }
  ): Promise<{ data: Investment[]; total: number; page: number }> {
    try {
      let investments = this.loadFromStorage();
      
      // Apply filters
      if (filters) {
        investments = this.applyFilters(investments, filters);
      }

      // Apply sorting
      if (sort) {
        investments = this.applySorting(investments, sort);
      }

      const total = investments.length;

      // Apply pagination
      if (pagination) {
        const start = (pagination.page - 1) * pagination.limit;
        const end = start + pagination.limit;
        investments = investments.slice(start, end);
      }

      return {
        data: investments,
        total,
        page: pagination?.page || 1
      };
    } catch (error) {
      console.error('Error fetching investments:', error);
      throw new Error('Failed to fetch investments');
    }
  }

  static async getById(id: string): Promise<Investment | null> {
    try {
      const investments = this.loadFromStorage();
      return investments.find(inv => inv.id === id) || null;
    } catch (error) {
      console.error('Error fetching investment:', error);
      throw new Error('Failed to fetch investment');
    }
  }

  static async create(data: InvestmentFormData): Promise<Investment> {
    try {
      const validation = this.validateInvestment(data);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${Object.values(validation.errors).flat().join(', ')}`);
      }

      const investment: Investment = {
        id: this.generateId(),
        ...data,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 1
      };

      const investments = this.loadFromStorage();
      investments.push(investment);
      this.saveToStorage(investments);

      return investment;
    } catch (error) {
      console.error('Error creating investment:', error);
      throw error;
    }
  }

  static async update(id: string, data: Partial<InvestmentFormData>): Promise<Investment> {
    try {
      const investments = this.loadFromStorage();
      const index = investments.findIndex(inv => inv.id === id);
      
      if (index === -1) {
        throw new Error('Investment not found');
      }

      const validation = this.validateInvestment({ ...investments[index], ...data } as InvestmentFormData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${Object.values(validation.errors).flat().join(', ')}`);
      }

      const updatedInvestment: Investment = {
        ...investments[index],
        ...data,
        updatedAt: new Date().toISOString(),
        version: (investments[index].version || 1) + 1
      };

      investments[index] = updatedInvestment;
      this.saveToStorage(investments);

      return updatedInvestment;
    } catch (error) {
      console.error('Error updating investment:', error);
      throw error;
    }
  }

  static async delete(id: string): Promise<void> {
    try {
      const investments = this.loadFromStorage();
      const index = investments.findIndex(inv => inv.id === id);
      
      if (index === -1) {
        throw new Error('Investment not found');
      }

      investments.splice(index, 1);
      this.saveToStorage(investments);
    } catch (error) {
      console.error('Error deleting investment:', error);
      throw error;
    }
  }

  // Bulk Operations
  static async bulkOperation(operation: InvestmentBulkOperation): Promise<{ success: number; failed: number; errors: string[] }> {
    try {
      let success = 0;
      let failed = 0;
      const errors: string[] = [];

      for (const id of operation.investmentIds) {
        try {
          switch (operation.operation) {
            case 'delete':
              await this.delete(id);
              success++;
              break;
              
            case 'activate':
              await this.update(id, { isActive: true });
              success++;
              break;
              
            case 'deactivate':
              await this.update(id, { isActive: false });
              success++;
              break;
              
            case 'updateCategory':
              if (operation.data?.category) {
                await this.update(id, { category: operation.data.category });
                success++;
              } else {
                failed++;
                errors.push(`No category specified for ${id}`);
              }
              break;
              
            case 'addTags':
              if (operation.data?.tags) {
                const investment = await this.getById(id);
                if (investment) {
                  const newTags = [...(investment.tags || []), ...operation.data.tags];
                  await this.update(id, { tags: [...new Set(newTags)] });
                  success++;
                } else {
                  failed++;
                  errors.push(`Investment ${id} not found`);
                }
              } else {
                failed++;
                errors.push(`No tags specified for ${id}`);
              }
              break;
              
            case 'removeTags':
              if (operation.data?.tags) {
                const investment = await this.getById(id);
                if (investment) {
                  const newTags = (investment.tags || []).filter(tag => !operation.data?.tags?.includes(tag));
                  await this.update(id, { tags: newTags });
                  success++;
                } else {
                  failed++;
                  errors.push(`Investment ${id} not found`);
                }
              } else {
                failed++;
                errors.push(`No tags specified for ${id}`);
              }
              break;
              
            default:
              failed++;
              errors.push(`Unknown operation: ${operation.operation}`);
          }
        } catch (error) {
          failed++;
          errors.push(`Failed to process ${id}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      return { success, failed, errors };
    } catch (error) {
      console.error('Error in bulk operation:', error);
      throw error;
    }
  }


  // Import/Export
  static async exportInvestments(investmentIds?: string[]): Promise<string> {
    try {
      const investments = this.loadFromStorage();
      const toExport = investmentIds 
        ? investments.filter(inv => investmentIds.includes(inv.id))
        : investments;

      return JSON.stringify(toExport, null, 2);
    } catch (error) {
      console.error('Error exporting investments:', error);
      throw new Error('Failed to export investments');
    }
  }

  static async importInvestments(jsonData: string): Promise<InvestmentImportResult> {
    try {
      const data = JSON.parse(jsonData);
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format');
      }

      let success = 0;
      let failed = 0;
      const errors: { row: number; errors: string[] }[] = [];
      const warnings: { row: number; warnings: string[] }[] = [];

      for (let i = 0; i < data.length; i++) {
        try {
          const validation = this.validateInvestment(data[i]);
          if (!validation.isValid) {
            failed++;
            errors.push({
              row: i + 1,
              errors: Object.values(validation.errors).flat()
            });
          } else {
            await this.create(data[i]);
            success++;
            
            if (Object.keys(validation.warnings).length > 0) {
              warnings.push({
                row: i + 1,
                warnings: Object.values(validation.warnings).flat()
              });
            }
          }
        } catch (error) {
          failed++;
          errors.push({
            row: i + 1,
            errors: [error instanceof Error ? error.message : 'Unknown error']
          });
        }
      }

      return { success, failed, errors, warnings };
    } catch (error) {
      console.error('Error importing investments:', error);
      throw new Error('Failed to import investments');
    }
  }

  // Private helper methods
  private static loadFromStorage(): Investment[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
      
      // Initialize with default investments if none exist
      const enhanced = defaultInvestments.map(inv => ({
        ...inv,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 1,
        isActive: true,
        tags: [],
        implementationTime: 30,
        riskLevel: 'medium' as const,
        roi: 15,
        vendor: 'Internal',
        supportLevel: 'business-hours' as const
      }));
      
      this.saveToStorage(enhanced);
      return enhanced;
    } catch (error) {
      console.error('Error loading from storage:', error);
      return [];
    }
  }

  private static saveToStorage(investments: Investment[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(investments));
    } catch (error) {
      console.error('Error saving to storage:', error);
      throw new Error('Failed to save investments');
    }
  }


  private static applyFilters(investments: Investment[], filters: InvestmentFilters): Investment[] {
    return investments.filter(investment => {
      if (filters.category && investment.category !== filters.category) return false;
      if (filters.costMin && investment.cost < filters.costMin) return false;
      if (filters.costMax && investment.cost > filters.costMax) return false;
      if (filters.riskLevel && investment.riskLevel !== filters.riskLevel) return false;
      if (filters.supportLevel && investment.supportLevel !== filters.supportLevel) return false;
      if (filters.isActive !== undefined && investment.isActive !== filters.isActive) return false;
      if (filters.tags?.length && !filters.tags.some(tag => investment.tags?.includes(tag))) return false;
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (!investment.name.toLowerCase().includes(search) && 
            !investment.description.toLowerCase().includes(search)) return false;
      }
      return true;
    });
  }

  private static applySorting(investments: Investment[], sort: InvestmentSortOptions): Investment[] {
    return [...investments].sort((a, b) => {
      let aVal: any = a[sort.field];
      let bVal: any = b[sort.field];

      if (sort.field === 'createdAt' || sort.field === 'updatedAt') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }

      if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  static validateInvestment(data: InvestmentFormData): InvestmentValidationResult {
    const errors: Record<string, string[]> = {};
    const warnings: Record<string, string[]> = {};

    // Required fields
    if (!data.name?.trim()) errors.name = ['Name is required'];
    if (!data.description?.trim()) errors.description = ['Description is required'];
    if (!data.icon?.trim()) errors.icon = ['Icon is required'];
    if (!data.category) errors.category = ['Category is required'];
    if (data.cost === undefined || data.cost < 0) errors.cost = ['Cost must be a positive number'];

    // Impact validation
    if (!data.impact) {
      errors.impact = ['Impact values are required'];
    } else {
      if (data.impact.growth < 0 || data.impact.growth > 100) errors.impact = [...(errors.impact || []), 'Growth must be 0-100'];
      if (data.impact.stability < 0 || data.impact.stability > 100) errors.impact = [...(errors.impact || []), 'Stability must be 0-100'];
      if (data.impact.riskReduction < 0 || data.impact.riskReduction > 100) errors.impact = [...(errors.impact || []), 'Risk reduction must be 0-100'];
      if (data.impact.socialBenefit < 0 || data.impact.socialBenefit > 100) errors.impact = [...(errors.impact || []), 'Social benefit must be 0-100'];
    }

    // Warnings
    if (data.cost > 1000000) warnings.cost = ['High cost investment - consider budget implications'];
    if (data.implementationTime > 365) warnings.implementationTime = ['Long implementation time - consider project timeline'];
    if (data.roi < 5) warnings.roi = ['Low ROI - verify investment value'];

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
      warnings
    };
  }

  private static generateId(): string {
    return `inv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}